// eslint-disable-next-line
import {injectable} from "inversify";
import {
    IChangeShippingOptionApproveRequest,
    IFetchShippingDetailsResponse,
    IFetchShippingRatesRequest,
    IResolveLabelErrorApproveRequest,
    IRma,
    IRmaAddNoteRequest,
    IValidateItemsActionRequest,
    IRmaApproveRequest,
    IRmaData,
    IReceiveShipmentRequest,
    IRmaReopenReuest,
    IRmaRejectRequest,
    IRmaCancelRequest,
    IRestockRequest, IRmaRejectItemRequest,
} from "../../pages/manage-returns/rma-page/rma-types";
import { IPortalDynamicShippingOptionDto } from "./create-rma-service";
import {rootContainer, TYPE} from "../container/root";
import {IApiResponse, IHttpService} from "../http-service";


@injectable()
class RmaListService {
	private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    public async rejectItem(id: number, data: IRmaRejectItemRequest): Promise<IRmaData> {
        const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/reject-item`, data);
        return res.payload;
    }

	public async fetchRma(id: number): Promise<IRmaData> {
		const res = await this.httpService.get<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}`);
		return res.payload;
	}
    
	/**
	 * Silent for the resolve-label-error flow: it reports the outcome with its own toasts, and the
	 * global error modal over a failed approve would leave the merchant nothing to retry from.
	 */
	public async approve(
		id: number,
		data: IRmaApproveRequest | IResolveLabelErrorApproveRequest | IChangeShippingOptionApproveRequest,
		options?: { silent?: boolean },
	): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(
			`/rms-api/manage-returns/rma/${id}/approve`,
			data,
			options,
		);
		return res.payload;
	}

	public async receiveShipment(id: number, data: IReceiveShipmentRequest): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/receive-shipment`, data);
		return res.payload;
	}

	public async reopen(id: number, data: IRmaReopenReuest): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/reopen`, data);
        return res.payload;
	}
    
	public async reject(id: number, data: IRmaRejectRequest): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/reject`, data);
		return res.payload;
	}
	
    public async cancel(id: number, data: IRmaCancelRequest): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/cancel`, data);
        return res.payload;
	}
    
	public async markAsDone(id: number): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/mark-as-done`);
		return res.payload;
	}
 
	public async resendStatusEmail(id: number, emailLogId: number): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/resend-status-email/${emailLogId}`);
		return res.payload;
	}

    public async releaseExchange(id: number): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/release-exchange`);
		return res.payload;
	}

	public async restock(id: number, data: IRestockRequest): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/restock`, data);
		return res.payload;
	}

	public async addNote(id: number, data: IRmaAddNoteRequest): Promise<Pick<IRma, 'timeline'>> {
		const res = await this.httpService.put<IApiResponse<Pick<IRma, 'timeline'>>>(`/rms-api/manage-returns/rma/${id}/add-note`, data);
		return res.payload;
	}
    
    
    public async saveValidation(id:number, data: IValidateItemsActionRequest) {
        const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/save-validation`, data);
        return res.payload;
    }

    public async saveValidationAll(id:number) {
        const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/save-validation-all`);
        return res.payload;
    }

    public async resolveErrors(id: number): Promise<IRmaData> {
		const res = await this.httpService.put<IApiResponse<IRmaData>>(`/rms-api/manage-returns/rma/${id}/resolve-errors`);
		return res.payload;
	}

    /** Fire-and-forget friendly: records download-label audit (RGET-3112). */
    public async auditDownloadLabel(rmaId: number, shipmentId: number): Promise<void> {
        await this.httpService.post<IApiResponse<{ ok: boolean }>>(
            `/rms-api/manage-returns/rma/${rmaId}/shipments/${shipmentId}/audit-download-label`
        );
    }

    /** Silent: the resolve-label-error modal renders the failure itself instead of the error modal. */
    public async fetchShippingDetails(id: number): Promise<IFetchShippingDetailsResponse> {
        const res = await this.httpService.get<IApiResponse<IFetchShippingDetailsResponse>>(
            `/rms-api/manage-returns/rma/${id}/fetch-shipping-details`,
            undefined,
            { silent: true },
        );
        return res.payload;
    }

    /**
     * Re-quotes the carrier on the details the merchant edited. Silent for the same reason as the
     * prefetch: the modal answers for its own failures.
     */
    public async fetchShippingRates(
        id: number,
        body: IFetchShippingRatesRequest,
    ): Promise<IPortalDynamicShippingOptionDto[]> {
        const res = await this.httpService.post<IApiResponse<IPortalDynamicShippingOptionDto[]>>(
            `/rms-api/manage-returns/rma/${id}/fetch-shipping-rates`,
            body,
            { silent: true },
        );
        return res.payload;
    }

    /**
     * Moves the RMA onto one return method other than its current one and quotes that method on
     * its own defaults. A PUT because the move is the point: it happens whether or not the
     * carrier prices the method, and it needs the permission approving needs.
     *
     * Silent for the same reason as the other two calls of the flow: the modal renders its own
     * failure.
     */
    public async changeReturnMethodAndFetchShippingRates(
        id: number,
        returnMethodId: number,
    ): Promise<IPortalDynamicShippingOptionDto[]> {
        const res = await this.httpService.put<IApiResponse<IPortalDynamicShippingOptionDto[]>>(
            `/rms-api/manage-returns/rma/${id}/return-methods/${returnMethodId}/shipping-rates`,
            undefined,
            { silent: true },
        );
        return res.payload;
    }
}


export default RmaListService;
